import { Box, Collapse, List } from '@mui/material';
import { useMatchRoute } from '@tanstack/react-router';
import useCollapseDrawer from 'hooks/useCollapseDrawer';
import { useEffect, useRef, useState } from 'react';
import { NavItemRoot, NavItemSub } from './NavItem';
import type { NavListProps } from './type';

type NavListRootProps = {
  list: NavListProps;
};

export function NavListRoot({ list }: NavListRootProps) {
  const { isCollapse, onToggleCollapse } = useCollapseDrawer();
  const matchRoute = useMatchRoute();
  const toList = [
    list.to,
    ...(Array.isArray(list.parentTo) ? list.parentTo : [list.parentTo]),
  ].filter(Boolean);
  const active = toList.some(
    (to) =>
      !!matchRoute({
        to: to as string, // tanstack router typing does not support partial paths
        fuzzy: true,
        includeSearch: false,
      })
  );

  const [open, setOpen] = useState(active);
  const rootRef = useRef<HTMLDivElement>(null);
  const shouldScrollAfterOpen = useRef(false);

  useEffect(() => {
    setOpen(active);
  }, [active]);

  // After the sidebar finishes opening (drawer transition = 300ms), scroll to this item
  useEffect(() => {
    if (!isCollapse && shouldScrollAfterOpen.current) {
      shouldScrollAfterOpen.current = false;
      const timer = setTimeout(() => {
        rootRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isCollapse]);

  if (list.children) {
    const onOpen: VoidFunction | undefined = !isCollapse
      ? () => setOpen(!open)
      : !list.to && !list.href
        ? () => {
            shouldScrollAfterOpen.current = true;
            onToggleCollapse();
            setOpen(true);
          }
        : undefined;

    return (
      <Box ref={rootRef}>
        <NavItemRoot item={list} active={active} open={open && !isCollapse} onOpen={onOpen} />
        {!isCollapse && (
          <Collapse in={open} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {list.children.map((item, index) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: no available id or key here
                <NavListSub key={index} list={item} />
              ))}
            </List>
          </Collapse>
        )}
      </Box>
    );
  }

  return <NavItemRoot item={list} active={active} />;
}

type NavListSubProps = {
  list: NavListProps;
};

function NavListSub({ list }: NavListSubProps) {
  const matchRoute = useMatchRoute();
  const active = !!matchRoute({ to: list.to, fuzzy: true, includeSearch: false });

  const [open, setOpen] = useState(active);

  useEffect(() => {
    setOpen(active);
  }, [active]);

  if (list.parentTo) {
    return (
      <>
        <NavItemSub item={list} onOpen={() => setOpen(!open)} open={open} active={active} />
        <Collapse in={open} timeout="auto" unmountOnExit>
          <List component="div" disablePadding sx={{ pl: 3 }}>
            {list.children.map((item, index) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: no available id or key here
              <NavItemSub key={index} item={item} />
            ))}
          </List>
        </Collapse>
      </>
    );
  }

  return <NavItemSub item={list} active={active} />;
}
